import React, { useState } from "react";
import AccountApiClient from "../../services/AccountApiClient";
import { Account } from "../../types";

interface AddEditAccountProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (account: Account) => void;
  account: Account | null;
  businessId: string;
}

const AddEditAccount: React.FC<AddEditAccountProps> = ({
  isOpen,
  onClose,
  onSave,
  account,
  businessId,
}) => {
  const [formData, setFormData] = useState({
    identifier: account?.identifier || "",
    isActive: account?.isActive || true,
    mid: account?.mid || "",
    gmpw: account?.gmpw || "",
    paymentGateway: account?.paymentGateway || "",
    allowedMaxAmount: account?.allowedMaxAmount || 500,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [shouldShowPanel, setShouldShowPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Handle slide animation
  React.useEffect(() => {
    if (isOpen) {
      // Add the open class on the next tick to trigger the transition
      const timer = setTimeout(() => {
        setShouldShowPanel(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShouldShowPanel(false);
    }
  }, [isOpen]);

  const handleInputChange = (key: string, value: string | boolean | number) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Identifier is only required for new accounts (add mode)
    if (!account && !formData.identifier.trim()) {
      newErrors.identifier = "Account identifier is required";
    } else if (!account && formData.identifier.trim().length < 4) {
      newErrors.identifier = "Identifier must be at least 4 characters";
    }

    if (!formData.paymentGateway.trim()) {
      newErrors.paymentGateway = "Payment gateway is required";
    }

    if (!formData.mid.trim()) {
      newErrors.mid = "MID is required";
    } else if (formData.mid.trim().length < 6) {
      newErrors.mid = "MID must be at least 6 characters";
    }

    if (!formData.gmpw.trim()) {
      newErrors.gmpw = "GMPW is required";
    } else if (formData.gmpw.trim().length < 6) {
      newErrors.gmpw = "GMPW must be at least 6 characters";
    }

    if (!formData.allowedMaxAmount || formData.allowedMaxAmount <= 0) {
      newErrors.allowedMaxAmount = "Allowed max amount must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      setIsLoading(true);
      setSuccessMessage("");
      setErrorMessage("");

      try {
        if (account) {
          // Editing existing account
          const response = await AccountApiClient.updateAccount(
            businessId,
            account.id,
            {
              mid: formData.mid,
              gmpw: formData.gmpw,
              paymentGateway: formData.paymentGateway,
              allowedMaxAmount: formData.allowedMaxAmount,
              isActive: formData.isActive,
            }
          );

          if (response.status === 200 || response.status === 201) {
            setSuccessMessage("Account updated successfully!");

            // Close the modal after showing success message for 2 seconds
            setTimeout(() => {
              setSuccessMessage("");

              const updatedAccount: Account = {
                ...account,
                ...formData,
              };
              onSave(updatedAccount);
              onClose();
            }, 2000);
          }
        } else {
          // Adding new account (always active for new accounts)
          const response = await AccountApiClient.addAccount(businessId, {
            identifier: formData.identifier,
            mid: formData.mid,
            gmpw: formData.gmpw,
            paymentGateway: formData.paymentGateway,
            allowedMaxAmount: formData.allowedMaxAmount,
          });

          if (response.status === 200 || response.status === 201) {
            setSuccessMessage("Account added successfully!");

            // Close the modal after showing success message for 2 seconds
            setTimeout(() => {
              setSuccessMessage("");

              // Call onSave with the new account data to refresh the parent list
              const newAccount: Account = {
                id: response.data?.id || Date.now().toString(),
                businessId: businessId,
                ...formData,
                isActive: true, // New accounts are always active
                terminals: [],
              };
              onSave(newAccount);

              // Close the modal
              onClose();
            }, 2000);
          }
        }
      } catch (error: any) {
        setErrorMessage(error.message || "Failed to save account");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    // Reset form to original values
    if (account) {
      setFormData({
        identifier: account.identifier || "",
        isActive: account.isActive || true,
        mid: account.mid || "",
        gmpw: account.gmpw || "",
        paymentGateway: account.paymentGateway || "",
        allowedMaxAmount: account.allowedMaxAmount || 500,
      });
    } else {
      // Reset form for new account
      setFormData({
        identifier: "",
        isActive: true,
        mid: "",
        gmpw: "",
        paymentGateway: "",
        allowedMaxAmount: 500,
      });
    }
    setErrors({});
    setSuccessMessage("");
    setErrorMessage("");
    onClose();
  };

  // Update form data when account changes
  React.useEffect(() => {
    if (account) {
      setFormData({
        identifier: account.identifier || "",
        isActive: account.isActive || true,
        mid: account.mid || "",
        gmpw: account.gmpw || "",
        paymentGateway: account.paymentGateway || "",
        allowedMaxAmount: account.allowedMaxAmount || 500,
      });
    } else {
      // Reset form for new account
      setFormData({
        identifier: "",
        isActive: true,
        mid: "",
        gmpw: "",
        paymentGateway: "",
        allowedMaxAmount: 500,
      });
    }
    setErrors({});
    setErrorMessage("");
    setSuccessMessage("");
  }, [account]);

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="slide-overlay" onClick={handleCancel}></div>}

      {/* Slide Panel */}
      {isOpen && (
        <div
          className={`slide-panel ${shouldShowPanel ? "slide-panel-open" : ""}`}
        >
          <div className="slide-panel-header">
            <h2>{account ? "✏️ Edit Account" : "➕ Add Account"}</h2>
            <button className="slide-panel-close" onClick={handleCancel}>
              ✕
            </button>
          </div>

          <div className="slide-panel-content">
            {successMessage && (
              <div
                className="alert alert-success"
                style={{
                  background: "#d4edda",
                  color: "#155724",
                  border: "1px solid #c3e6cb",
                  borderRadius: "4px",
                  padding: "12px",
                  marginBottom: "16px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                ✅ {successMessage}
              </div>
            )}

            {errorMessage && (
              <div
                className="alert alert-error"
                style={{
                  background: "#f8d7da",
                  color: "#721c24",
                  border: "1px solid #f5c6cb",
                  borderRadius: "4px",
                  padding: "12px",
                  marginBottom: "16px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                ❌ {errorMessage}
              </div>
            )}

            {errors.general && (
              <div
                className="alert alert-error"
                style={{
                  background: "#f8d7da",
                  color: "#721c24",
                  border: "1px solid #f5c6cb",
                  borderRadius: "4px",
                  padding: "12px",
                  marginBottom: "16px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                ❌ {errors.general}
              </div>
            )}

            <div className="form-group">
              <label>Account Identifier {!account && "*"}</label>
              <input
                type="text"
                value={formData.identifier}
                onChange={(e) =>
                  handleInputChange("identifier", e.target.value)
                }
                placeholder="e.g., Main Account"
                minLength={4}
                className={`form-input ${
                  errors.identifier ? "form-input-error" : ""
                }`}
                disabled={!!account}
                style={
                  account
                    ? {
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed",
                        color: "#6b7280",
                      }
                    : {}
                }
              />
              {errors.identifier && (
                <span className="form-error">{errors.identifier}</span>
              )}
              {account && (
                <small style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                  Identifier cannot be changed when editing
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Payment Gateway *</label>
              <select
                value={formData.paymentGateway}
                onChange={(e) =>
                  handleInputChange("paymentGateway", e.target.value)
                }
                className={`form-input ${
                  errors.paymentGateway ? "form-input-error" : ""
                }`}
              >
                <option value="">Select Payment Gateway</option>
                <option value="C9">C9</option>
                <option value="NMI">NMI</option>
              </select>
              {errors.paymentGateway && (
                <span className="form-error">{errors.paymentGateway}</span>
              )}
            </div>

            <div className="form-group">
              <label>MID (Merchant ID) *</label>
              <input
                type="text"
                value={formData.mid}
                onChange={(e) => handleInputChange("mid", e.target.value)}
                placeholder="e.g., 1234567890"
                minLength={6}
                className={`form-input ${errors.mid ? "form-input-error" : ""}`}
              />
              {errors.mid && <span className="form-error">{errors.mid}</span>}
            </div>

            <div className="form-group">
              <label>GMPW (Gateway Merchant Password) *</label>
              <input
                type="text"
                value={formData.gmpw}
                onChange={(e) => handleInputChange("gmpw", e.target.value)}
                placeholder="Enter GMPW"
                minLength={6}
                className={`form-input ${
                  errors.gmpw ? "form-input-error" : ""
                }`}
              />
              {errors.gmpw && <span className="form-error">{errors.gmpw}</span>}
            </div>

            <div className="form-group">
              <label>Allowed Max Amount *</label>
              <input
                type="number"
                value={formData.allowedMaxAmount}
                onChange={(e) =>
                  handleInputChange("allowedMaxAmount", Number(e.target.value))
                }
                placeholder="e.g., 500"
                min="1"
                className={`form-input ${
                  errors.allowedMaxAmount ? "form-input-error" : ""
                }`}
              />
              {errors.allowedMaxAmount && (
                <span className="form-error">{errors.allowedMaxAmount}</span>
              )}
            </div>

            {/* Only show Status checkbox when editing existing account */}
            {account && (
              <div className="form-group">
                <div
                  className="checkbox-group"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      handleInputChange("isActive", e.target.checked)
                    }
                    className="checkbox-input"
                    style={{ margin: "0", width: "16px", height: "16px" }}
                  />
                  <label
                    className="checkbox-label"
                    style={{ margin: "0", cursor: "pointer" }}
                  >
                    Account is Active
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="slide-panel-footer">
            <button
              className="btn secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="btn primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading
                ? "Saving..."
                : account
                ? "Save Changes"
                : "Add Account"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddEditAccount;
